import { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CalendarView = ({ selectedDate, onDateSelect, blockedDates, bookedDates }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const arabicDays = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateKey = (date) => {
    if (!date) return '';
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    let day = String(date?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    const dateKey = formatDateKey(date);
    
    const isBlocked = blockedDates?.some(bd => bd?.date === dateKey);
    const isBooked = bookedDates?.some(bd => bd?.date === dateKey);
    const isPast = date < new Date()?.setHours(0, 0, 0, 0);
    
    if (isBlocked) return 'blocked';
    if (isBooked) return 'booked';
    if (isPast) return 'past';
    return 'available';
  };

  const getBookingInfo = (date) => {
    if (!date) return null;
    const dateKey = formatDateKey(date);
    return bookedDates?.find(bd => bd?.date === dateKey);
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={nextMonth}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
        >
          <Icon name="ChevronRight" size={20} className="text-slate-700" />
        </button>
        
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-800 via-blue-700 to-blue-600 bg-clip-text text-transparent">
          {arabicMonths?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h2>
        
        <button
          onClick={previousMonth}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
        >
          <Icon name="ChevronLeft" size={20} className="text-slate-700" />
        </button>
      </div>
      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {arabicDays?.map((day) => (
          <div key={day} className="text-center text-sm font-semibold text-slate-600 py-2">
            {day}
          </div>
        ))}
      </div>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days?.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const status = getDateStatus(date);
          const bookingInfo = getBookingInfo(date);
          const isSelected = selectedDate && formatDateKey(date) === formatDateKey(selectedDate);

          let bgClass = 'bg-white hover:bg-slate-50';
          let textClass = 'text-slate-900';
          let borderClass = 'border-slate-200';

          if (status === 'blocked') {
            bgClass = 'bg-red-50 hover:bg-red-100';
            textClass = 'text-red-700';
            borderClass = 'border-red-300';
          } else if (status === 'booked') {
            bgClass = 'bg-blue-50 hover:bg-blue-100';
            textClass = 'text-blue-700';
            borderClass = 'border-blue-300';
          } else if (status === 'past') {
            bgClass = 'bg-slate-50';
            textClass = 'text-slate-400';
            borderClass = 'border-slate-100';
          } else {
            bgClass = 'bg-green-50 hover:bg-green-100';
            textClass = 'text-green-700';
            borderClass = 'border-green-300';
          }

          if (isSelected) {
            borderClass = 'border-2 border-blue-500 ring-2 ring-blue-200';
          }

          return (
            <button
              key={formatDateKey(date)}
              onClick={() => onDateSelect(date)}
              className={`aspect-square rounded-lg border ${bgClass} ${borderClass} p-2 transition-all duration-200 hover:scale-105 flex flex-col items-center justify-center relative group`}
              disabled={status === 'past'}
            >
              <span className={`text-lg font-semibold ${textClass}`}>
                {date?.getDate()}
              </span>
              {status === 'booked' && bookingInfo && (
                <span className="text-xs text-blue-600 mt-1 truncate w-full text-center">
                  {bookingInfo?.reference}
                </span>
              )}
              {status === 'blocked' && (
                <Icon name="Lock" size={12} className="text-red-500 mt-1" />
              )}
              {/* Tooltip */}
              {bookingInfo && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  <div className="font-semibold">{bookingInfo?.reference}</div>
                  <div>{bookingInfo?.client}</div>
                </div>
              )}
            </button>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-6 pt-6 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-sm text-slate-600">متاح</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500"></div>
          <span className="text-sm text-slate-600">محجوز</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500"></div>
          <span className="text-sm text-slate-600">محجوب</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-slate-300"></div>
          <span className="text-sm text-slate-600">ماضي</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;